import { useState, useEffect, useCallback, useRef } from "react";
import {
  getPostComments,
  getCommentReplies,
  createComment,
  likeComment,
  unlikeComment,
  deleteComment,
} from "../../../services/commentService";
import { useQueryClient } from "@tanstack/react-query";

export const useComments = (postId, apiCall, queryKey = ["feedPosts", "for_you"]) => {
  const queryClient = useQueryClient();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const inputRef = useRef(null);

  // Load initial comments
  useEffect(() => {
    const fetchComments = async () => {
      try {
        setLoading(true);
        const result = await getPostComments(apiCall, postId, 20, null);
        setComments(result.comments);
        setHasMore(result.hasMore);
        setCursor(result.nextCursor);
        setError(null);
      } catch (err) {
        setError("Failed to load comments");
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [postId, apiCall]);

  // Auto-focus input when hook initializes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // Load more comments
  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore) return;

    try {
      setLoadingMore(true);
      const result = await getPostComments(apiCall, postId, 20, cursor);

      setComments((prev) => {
        const existingIds = new Set(prev.map((c) => c._id));
        const newComments = result.comments.filter(
          (comment) => !existingIds.has(comment._id)
        );
        return [...prev, ...newComments];
      });
      setHasMore(result.hasMore);
      setCursor(result.nextCursor);
    } catch (err) {
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, apiCall, postId, cursor]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const threshold = 50;
      const isNearBottom = scrollHeight - scrollTop - clientHeight <= threshold;

      if (isNearBottom && hasMore && !loadingMore && !loading) {
        loadMore();
      }
    },
    [hasMore, loadingMore, loading, loadMore]
  );

  // Handle reply posted
  const handleReplyPosted = useCallback((parentCommentId, newReply) => {
    setComments((prev) =>
      prev.map((comment) => {
        if (comment._id === parentCommentId) {
          return {
            ...comment,
            stats: {
              ...comment.stats,
              replies: (comment.stats?.replies || 0) + 1,
            },
          };
        }
        return comment;
      })
    );
  }, []);

  // Post comment
  const handlePostComment = async (user) => {
    if (!newComment.trim() || posting) return;

    const previousComments = [...comments];
    let tempComment = null;

    try {
      setPosting(true);
      const parentCommentId = replyingTo?._id;

      const currentUser = {
        _id: user?.profile?._id,
        username: user?.profile?.username,
        profilePicture: user?.profile?.profilePicture,
      };

      tempComment = {
        _id: `temp-${Date.now()}`,
        post: postId,
        author: {
          _id: currentUser._id,
          username: currentUser.username,
          profilePicture: currentUser.profilePicture,
        },
        content: newComment
          .replace(`@${replyingTo?.author?.username} `, "")
          .trim(),
        createdAt: new Date().toISOString(),
        stats: {
          likes: 0,
          replies: 0,
        },
        isLikedByUser: false,
      };

      // Optimistic update
      if (parentCommentId) {
        if (replyingTo?.onReplyPostedLocally) {
          replyingTo.onReplyPostedLocally(tempComment);
        }
        handleReplyPosted(parentCommentId, tempComment);
      } else {
        setComments((prev) => [tempComment, ...prev]);
      }

      // Update React Query cache
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData?.pages) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            posts: page.posts.map((post) => {
              if (post._id === postId) {
                return {
                  ...post,
                  stats: {
                    ...post.stats,
                    comments: (post.stats?.comments || 0) + 1,
                  },
                };
              }
              return post;
            }),
          })),
        };
      });

      setNewComment("");
      setReplyingTo(null);

      if (inputRef.current) {
        inputRef.current.focus();
      }

      // Make API call
      const result = await createComment(
        apiCall,
        postId,
        newComment,
        parentCommentId || null
      );

      // Replace temporary comment with real one
      if (parentCommentId) {
        if (replyingTo?.replaceReplyWithReal) {
          replyingTo.replaceReplyWithReal(tempComment._id, result.comment);
        }
      } else {
        setComments((prev) =>
          prev.map((comment) =>
            comment._id === tempComment._id ? result.comment : comment
          )
        );
      }
    } catch (error) {
      // Rollback
      setComments(previousComments);

      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData?.pages) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            posts: page.posts.map((post) => {
              if (post._id === postId) {
                return {
                  ...post,
                  stats: {
                    ...post.stats,
                    comments: Math.max(0, (post.stats?.comments || 1) - 1),
                  },
                };
              }
              return post;
            }),
          })),
        };
      });

      setActionError(error.message || "Failed to post comment. Please try again.");
    } finally {
      setPosting(false);
    }
  };

  // Handle reply
  const handleReply = useCallback(
    (comment, onReplyPostedLocally, replaceReplyWithReal) => {
      setReplyingTo({
        ...comment,
        onReplyPostedLocally,
        replaceReplyWithReal,
      });
      setNewComment(`@${comment.author.username} `);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    []
  );

  // Handle like toggle
  const handleLikeToggle = useCallback(
    async (commentId, shouldLike) => {
      try {
        if (shouldLike) {
          await likeComment(apiCall, commentId);
        } else {
          await unlikeComment(apiCall, commentId);
        }
      } catch (error) {
        setActionError(error.message || "We couldn't update your like. Please try again.");
        throw error;
      }
    },
    [apiCall]
  );

  // Show action menu
  const handleShowActionMenu = useCallback((commentId) => {
    setActiveCommentId(commentId);
  }, []);

// Delete comment
const handleDeleteComment = useCallback(
  async (commentId) => {
    const comment = comments.find((c) => c._id === commentId);
    const previousComments = [...comments];
    
    // If comment not found in main array, it's a reply - just delete it
    const isReply = !comment;
    const deletedRepliesCount = comment?.stats?.replies || 0;
    const totalToDelete = isReply ? 1 : 1 + deletedRepliesCount;

    try {
      // Remove from local state only if it's a parent comment
      if (!isReply) {
        setComments((prev) => {
          return prev.filter((c) => c._id !== commentId);
        });
      }

      // Update React Query cache - decrement post comment count
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData?.pages) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            posts: page.posts.map((post) => {
              if (post._id === postId) {
                return {
                  ...post,
                  stats: {
                    ...post.stats,
                    comments: Math.max(
                      0,
                      (post.stats?.comments || 1) - totalToDelete
                    ),
                  },
                };
              }
              return post;
            }),
          })),
        };
      });

      setActiveCommentId(null);

      // Make API call
      await deleteComment(apiCall, commentId);
    } catch (error) {
      // Rollback
      setComments(previousComments);
      queryClient.invalidateQueries({ queryKey });
      setActionError(error.message || "Failed to delete comment. Please try again.");
      throw error; 
    }
  },
  [apiCall, comments, queryClient, postId, queryKey]
);

  return {
    comments,
    setComments,
    loading,
    error,
    actionError,
    setActionError,
    hasMore,
    loadingMore,
    newComment,
    setNewComment,
    posting,
    replyingTo,
    setReplyingTo,
    activeCommentId,
    setActiveCommentId,
    inputRef,
    handleScroll,
    handlePostComment,
    handleReply,
    handleLikeToggle,
    handleShowActionMenu,
    handleDeleteComment,
    handleReplyPosted,
  };
};